import { memo, useState, useCallback, useRef, useEffect } from "react";
import { Modal } from "rsuite";
import { Modal as ConfirmModal } from "../../../components";
import BestStartButton from "../../../components/button";
import { IoSendOutline } from "react-icons/io5";
import { MdDeleteOutline } from "react-icons/md";

function FeedbackDialog({
  open,
  feedbacks,
  kpiDataId,
  currentUserEmail,
  isManager,
  isSending,
  isDeleting,
  onClose,
  onSubmit,
  onDelete,
}) {
  const [message, setMessage] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, feedbacks]);

  const handleSubmit = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed || !kpiDataId || isSending) {
      return;
    }

    onSubmit({ kpiDataId, feedback: trimmed });
    setMessage("");
  }, [message, kpiDataId, isSending, onSubmit]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleClose = useCallback(() => {
    if (isSending) {
      return;
    }
    setMessage("");
    onClose();
  }, [isSending, onClose]);

  const openConfirmDelete = useCallback((id) => setConfirmDeleteId(id), []);
  const closeConfirmDelete = useCallback(() => setConfirmDeleteId(null), []);

  const handleConfirmDelete = useCallback(() => {
    if (confirmDeleteId) {
      onDelete(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  }, [confirmDeleteId, onDelete]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) {
      return "";
    }
    const date = new Date(dateString);
    return date.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  }, []);

  const sortedFeedbacks = [...(feedbacks ?? [])].sort(
    (a, b) => new Date(a.createdAt ?? 0) - new Date(b.createdAt ?? 0)
  );

  return (
    <>
      <Modal
        backdrop="static"
        keyboard={!isSending && !isDeleting}
        open={open}
        onClose={handleClose}
        size="sm"
        className="feedback-dialog"
      >
        <Modal.Header>
          <Modal.Title>Scorecard Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="feedback-messages">
            {sortedFeedbacks.length === 0 && (
              <p className="feedback-empty">No feedback yet.</p>
            )}
            {sortedFeedbacks.map((item) => {
              const isOwn = currentUserEmail && item.createdBy?.toLowerCase() === currentUserEmail.toLowerCase();
              return (
              <div className={`feedback-bubble ${isOwn ? "feedback-bubble-own" : "feedback-bubble-other"}`} key={item.id}>
                <div className="feedback-bubble-header">
                  <span className="feedback-bubble-from">{item.createdBy}</span>
                  {isOwn && (
                    <button
                      className="feedback-delete-button"
                      aria-label="Delete feedback"
                      onClick={() => openConfirmDelete(item.id)}
                      disabled={isDeleting}
                    >
                      <MdDeleteOutline size={14} />
                    </button>
                  )}
                </div>
                <p className="feedback-bubble-text">{item.feedback}</p>
                <span className="feedback-bubble-time">{formatDate(item.createdAt)}</span>
              </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="feedback-input-row">
              <textarea
                className="feedback-input"
                rows={2}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your feedback..."
                disabled={isSending}
              />
              <BestStartButton
                aria-label="Send feedback"
                color="green"
                content={<IoSendOutline size={18} />}
                height={38}
                width={38}
                onClick={handleSubmit}
                disabled={isSending || !message.trim()}
              />
            </div>
        </Modal.Body>
      </Modal>

      <ConfirmModal
        title="Delete feedback?"
        text="This feedback message will be permanently removed."
        confirmButtonText="Delete"
        cancelButtonText="Keep"
        confirmColor="red"
        cancelColor="blue"
        onConfirm={handleConfirmDelete}
        onClose={closeConfirmDelete}
        open={confirmDeleteId !== null}
      />
    </>
  );
}

export default memo(FeedbackDialog);
